import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, usePermissions, useTranslate } from "@refinedev/core";
import { Button, Field, Modal } from "@fawkes/ui";

import { formatDate, todayIso } from "../../i18n/format";
import { COMPONENT_PER_SLEUTEL } from "./dierComponenten";
import { DierOnderdeel } from "./DierOnderdeel";

/**
 * Het onderdeel Gewicht: de laatste weging, met een plus die er een nieuwe bij zet.
 *
 * <p>De eerste van de twaalf, en meteen het patroon voor de rest: lees de nieuwste registratie uit de
 * eigen bron, toon waarde en herkomst, en roep voor de invoer dezelfde service aan als het volledige
 * tabblad. Na opslaan wordt alleen dit onderdeel opnieuw geladen -- het dashboard blijft staan.
 *
 * <p><b>Twee wegen naar binnen, en dat is geen toeval.</b> Wegen is verzorgerswerk: het getal invullen
 * en klaar, met vandaag als datum. Een weging achteraf invoeren of corrigeren is beheer, want dan
 * verschuif je iets wat er al stond. De api maakt dat onderscheid al ({@code /weights/registreer} tegen
 * {@code /weights}); het scherm volgt het in plaats van er zijn eigen regel naast te zetten. Wie mag
 * corrigeren krijgt dus een datumveld, wie niet mag legt vast op vandaag.
 */

interface Meting {
  id: number;
  datum: string;
  gewicht: number;
  opmerking: string | null;
}

export function GewichtOnderdeel({ dierId }: { dierId: number }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data: rechten } = usePermissions<string[]>();
  const magCorrigeren = (rechten ?? []).includes("ROLE_ANIMAL_EDIT");

  const { data, refetch } = useCustom<{ metingen: Meting[] }>({
    url: `animals/${dierId}/weights`,
    method: "get",
  });
  const laatste = useMemo(() => {
    const metingen = data?.data?.metingen;
    if (!Array.isArray(metingen) || metingen.length === 0) return null;
    // Nieuwste eerst; de api sorteert oplopend voor de grafiek.
    return [...metingen].sort((a, b) => b.datum.localeCompare(a.datum))[0];
  }, [data]);

  const [open, setOpen] = useState(false);

  return (
    <>
      <DierOnderdeel
        def={COMPONENT_PER_SLEUTEL.gewicht}
        waarde={laatste ? t("dierTegel.gewicht.kg", { args: [String(laatste.gewicht).replace(".", ",")] }) : null}
        herkomst={laatste ? formatDate(laatste.datum.slice(0, 10), locale) : undefined}
        onActie={() => setOpen(true)}
      />
      {open ? (
        <NieuweWeging
          dierId={dierId}
          magCorrigeren={magCorrigeren}
          onSluit={() => setOpen(false)}
          onOpgeslagen={() => { setOpen(false); refetch?.(); }}
        />
      ) : null}
    </>
  );
}

/**
 * Het snelle invoerscherm: alleen de velden die deze actie nodig heeft.
 *
 * <p>De gebruiker blijft bij het dier en ziet wat er na opslaan op de tegel komt te staan. Opslaan mag
 * pas als er een geldig getal staat -- een lege of onleesbare weging is geen registratie.
 */
export function NieuweWeging({ dierId, magCorrigeren, onSluit, onOpgeslagen }: {
  dierId: number;
  magCorrigeren: boolean;
  onSluit: () => void;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [gewicht, setGewicht] = useState("");
  const [datum, setDatum] = useState(todayIso());
  const [opmerking, setOpmerking] = useState("");
  const [fout, setFout] = useState<string | null>(null);

  const getal = (v: string): number | null => {
    const n = Number(v.replace(",", "."));
    return v.trim() !== "" && Number.isFinite(n) && n > 0 ? n : null;
  };
  const waarde = getal(gewicht);

  const bewaar = () => {
    if (waarde == null) {
      setFout(t("dierTegel.gewicht.ongeldig"));
      return;
    }
    setFout(null);
    // Met correctierecht mag de datum mee; zonder dat recht legt de api hem op vandaag vast.
    const opties = magCorrigeren
      ? {
          url: `animals/${dierId}/weights`,
          values: { datum, gewicht: waarde, opmerking: opmerking.trim() === "" ? null : opmerking.trim() },
        }
      : { url: `animals/${dierId}/weights/registreer`, values: { gewicht: waarde } };
    mutate(
      { ...opties, method: "post", successNotification: false, errorNotification: false },
      { onSuccess: onOpgeslagen, onError: () => setFout(t("dierTegel.gewicht.fout")) },
    );
  };

  return (
    <Modal open title={t("dierTegel.gewicht.nieuw")} onClose={onSluit}
      footer={
        <>
          <Button variant="secondary" onClick={onSluit}>{t("common.cancel")}</Button>
          <Button onClick={bewaar} disabled={bezig}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="do-invoer">
        <Field
          label={t("dierTegel.gewicht.label")}
          value={gewicht}
          inputMode="decimal"
          data-testid="weging-gewicht"
          onChange={(e) => setGewicht(e.target.value)}
        />
        {magCorrigeren ? (
          <Field
            label={t("dierTegel.gewicht.datum")}
            type="date"
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
          />
        ) : (
          // Zonder correctierecht staat de datum vast op vandaag; dat zeggen is eerlijker dan een veld
          // tonen dat toch genegeerd wordt.
          <p className="do-invoer__hint">{t("dierTegel.gewicht.vandaag")}</p>
        )}
        <Field
          label={t("dierTegel.gewicht.opmerking")}
          value={opmerking}
          onChange={(e) => setOpmerking(e.target.value)}
        />
        {waarde != null ? (
          <p className="do-invoer__voorbeeld">
            {t("dierTegel.gewicht.wordt", { args: [String(waarde).replace(".", ",")] })}
          </p>
        ) : null}
        {fout ? <p className="do-invoer__fout" role="alert">{fout}</p> : null}
      </div>
    </Modal>
  );
}
